/**
 * Easter party flyers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterPartyFlyersPack: ToilePack = {
    slug: 'easter-party-flyers',
    category: 'season',
    name: 'Easter party flyers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'hunt', label: 'Egg hunt', subject: 'A vertical flyer lettered "COMMUNITY EGG HUNT" over a meadow with baskets, a blank panel for date and place'},
        {key: 'fair', label: 'Spring fair', subject: 'A vertical flyer lettered "SPRING FAIR" with cartoon stalls and bunting, a blank panel for details'},
        {key: 'service', label: 'Easter service', subject: 'A vertical flyer lettered "EASTER SERVICE" over a lily and a sunrise, a blank panel for details'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for a neighborhood potluck instead', 'with a map to the venue', 'as a square social post'],
    directionHint: 'try: a neighborhood potluck instead',
    seo: {
        title: 'Easter Flyers: Egg Hunt, Fair & Church Event',
        metaDescription: "Nine Easter event flyers — an egg hunt, a spring fair, and a church service — with a blank details panel, in pastel, vintage, and kids' styles.",
        h1: 'Easter flyers: egg hunt, spring fair, church event',
        tagline: 'Nine flyers, three event types, three styles each — headline lettered, details left blank.',
        intro: "Three Easter event flyers — a community egg hunt, a spring fair, and a church service — each with the headline lettered and a blank panel for the organizer to fill in, in pastel, vintage, and kids' cartoon styles. Ask for a different event type, a QR-code space, or a sponsor line, and the same flyer grammar carries it.",
        useCases: [
            'a community egg-hunt announcement',
            'a church bulletin board',
            'a school spring fair',
            'a neighborhood noticeboard',
        ],
        faq: [
            {q: 'Is the date and location already filled in?', a: 'No — the details panel prints blank; write or print the date, time, and place in afterward.'},
            {q: 'What size do these print at?', a: 'Each flyer is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a different event type?', a: 'Yes — the direction field takes any variation, including a different event or venue.'},
            {q: 'What are the three styles?', a: "Pastel, vintage engraved-border, and bright kids' cartoon."},
        ],
    },
}
